/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES ENCRES ET LES OUTILS — VOCABULAIRE DE LA CHARTE, PAS DU MOTEUR.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Ces deux listes vivaient dans `features/cours/moteur.ts`, et la barrière de lint a
 * montré que c'était le mauvais endroit.** `BarreDOutils` est une primitive de
 * `shared/ui/` : en important le vocabulaire depuis une feature, elle faisait remonter une
 * primitive vers un domaine métier — précisément ce que `web/CLAUDE.md` ferme, et ce que
 * `boundaries/dependencies` refuse maintenant.
 *
 * **Le défaut n'était pas de rangement, il était de sens.** Les cinq encres sont **la
 * charte** : `DESIGN.md` les nomme, `tokens.css` les porte en `--encre-*`, et la barre
 * d'outils les montre « sous forme d'une pastille ronde pleine ». Elles ne sont pas une
 * propriété du moteur de tracé — **elles lui préexistent**, et elles lui survivront le jour
 * où D-002 est réévaluée et où le moteur change. C'est le moteur qui les consomme, pas
 * l'inverse, et la dépendance descend donc de la feature vers la primitive.
 *
 * **CE FICHIER NE CONTIENT AUCUNE VALEUR DE STYLE.** Les noms d'encre sont des clés ; leur
 * couleur vit dans `tokens.css` et nulle part ailleurs.
 */

/** Les cinq encres de la charte. « Il n'existe AUCUNE encre violette. » */
export const ENCRES = ['noir', 'bleu', 'rouge', 'vert', 'surligneur'] as const;
export type Encre = (typeof ENCRES)[number];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES OUTILS QUI S'ARMENT, ET F-22 FERME LA LISTE À DOUZE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Douze outils : crayon, gomme, surligneur, choix de couleur, choix d'épaisseur, texte
 * tapé au clavier, formes (trait, rectangle, cercle, flèche), sélection d'un ensemble
 * d'éléments, annuler et refaire, tout effacer, pointeur temporaire, pense-bête »
 * (Q347). **Le zoom et le mode main sont acquis par D-007**, donc ils ne comptent pas
 * dans les douze et le moteur les fournit.
 *
 * **CETTE LISTE NE PORTE QUE CE QUI S'ARME COMME OUTIL ACTIF, et les trois autres
 * familles sont ailleurs pour une raison de forme, pas de rangement.** Le choix de
 * couleur et le choix d'épaisseur sont des **réglages** — ils ne changent pas ce que fait
 * le prochain geste, ils changent son apparence. Annuler, refaire et tout effacer sont
 * des **gestes ponctuels** : ils s'exercent, ils ne se sélectionnent pas. Les mettre ici
 * obligerait la barre à traiter « l'outil actif est *annuler* », qui n'a pas de sens.
 *
 * **Les douze s'y retrouvent tous** : neuf ci-dessous, plus les deux réglages et les
 * trois gestes que la barre porte à côté — et `BarreDOutils` les compte.
 */
export const OUTILS = [
  'crayon',
  'surligneur',
  'gomme',
  'selection',
  'texte',
  'trait',
  'rectangle',
  'cercle',
  'fleche',
  'pointeur',
  'pense-bete',
] as const;
export type Outil = (typeof OUTILS)[number];
